// SPDX-License-Identifier: LicenseRef-Pachamama-Proprietary
// Copyright 2026 Pachamama Studios B.V. All rights reserved.

/**
 * AYNI — statistics on the pins' graph (research tier, §Next S1, S3, S6):
 * trends, time of emergence and correlation, as marks on the graph and a
 * few lines of results under it (`research/pinStats.ts`, `stats.ts`).
 *
 * The wording claims only what the tests support: "rising" or "falling"
 * when least squares and Mann–Kendall agree at 5 %; an interval with every
 * slope; how many independent values a result rests on; and every caution
 * that applies (a window not fully played, few independent values, tied
 * slopes, a seasonal or daily cycle, an ensemble statistic's too-small
 * noise).
 */

import type { Pin } from '../services/measure/pins'
import type { MeasurementSeries } from '../services/measure/series'
import {
  MIN_TREND_POINTS,
  defaultEmergencePeriod,
  formatP,
  pinCorrelation,
  pinEmergence,
  pinTrend,
  statsSeries,
  TREND_UNIT_MS,
  type EmergenceSettings,
  type PinEmergence,
  type PinTrend,
  type TrendUnit,
} from '../services/research/pinStats'
import { formatNumber } from '../i18n/format'
import { t, type MessageKey } from '../i18n'
import { escapeAttr, escapeHtml } from './domUtils'
import { snapToFrame } from '../services/research/frameTime'

type Cell = string | number | null

const PER_KEY: Readonly<Record<TrendUnit, MessageKey>> = {
  decade: 'research.trend.per.decade',
  year: 'research.trend.per.year',
  day: 'research.trend.per.day',
  hour: 'research.trend.per.hour',
}

/** "+0.213 °C". */
export function signedAmount(value: number, units: string | undefined): string {
  if (!Number.isFinite(value)) return '—'
  const text = formatNumber(Math.abs(value), { maximumSignificantDigits: 3 })
  const sign = value > 0 ? '+' : value < 0 ? '−' : ''
  return units ? `${sign}${text} ${units}` : `${sign}${text}`
}

function seriesKey(s: MeasurementSeries): string {
  return `${s.startMs}|${s.endMs}|${s.slots.map((x) => (x.sampled ? x.value ?? 'x' : '')).join(',')}`
}

const trendCache = new Map<string, { key: string; trend: PinTrend | null }>()

/** The pin's trend, recomputed only when its series changed. */
export function trendOf(pin: Pin): PinTrend | null {
  if (!pin.series) return null
  // AYNI: keyed on the steps the test runs on (at most 256), not every frame.
  const steps = statsSeries(pin.series)
  const key = seriesKey(steps)
  const hit = trendCache.get(pin.id)
  if (hit?.key === key) return hit.trend
  const trend = pinTrend(steps)
  trendCache.set(pin.id, { key, trend })
  return trend
}

export interface ChartFrame {
  readonly left: number
  readonly top: number
  readonly plotW: number
  readonly plot: number
  readonly min: number
  readonly max: number
  readonly startMs: number
  readonly endMs: number
}

function swatch(pin: Pin): string {
  return `<span class="tools-data-pin-swatch" style="--pin-color:${escapeAttr(pin.color)}" aria-hidden="true"></span>`
}

function block(head: string, lines: readonly string[]): string {
  return `<div class="tools-data-trend"><p class="tools-data-trend-name">${head}</p>${lines.map((l) => `<p class="tools-data-trend-line-text">${escapeHtml(l)}</p>`).join('')}</div>`
}

// ------------------------------------------------------------------ trend (§Next S1)

/** Sen's line for each pin with a trend, dashed in its colour, clipped to the plot. */
export function trendLinesSvg(pins: readonly Pin[], frame: ChartFrame, big: boolean): string {
  const range = frame.max - frame.min
  if (!(range > 0)) return ''
  const yAt = (v: number) => frame.top + (1 - (v - frame.min) / range) * frame.plot
  const xAt = (ms: number) => frame.left + ((ms - frame.startMs) / (frame.endMs - frame.startMs)) * frame.plotW
  const clipId = `trend-clip-${big ? 'w' : 'p'}`
  const lines = pins.map((pin) => {
    const trend = trendOf(pin)
    if (!trend) return ''
    const { line } = trend
    return `<line x1="${xAt(line.startMs).toFixed(1)}" y1="${yAt(line.startValue).toFixed(1)}" x2="${xAt(line.endMs).toFixed(1)}" y2="${yAt(line.endValue).toFixed(1)}" stroke="${escapeAttr(pin.color)}" stroke-width="${big ? 2 : 1.4}" stroke-dasharray="${big ? '6 4' : '4 3'}" class="tools-data-trend-line" clip-path="url(#${clipId})"/>`
  }).join('')
  if (!lines) return ''
  return `<defs><clipPath id="${clipId}"><rect x="${frame.left}" y="${frame.top}" width="${frame.plotW}" height="${frame.plot}"/></clipPath></defs>${lines}`
}

/** One block per pin: the verdict, both slopes with their intervals, the independent values, the cautions. */
export function trendBlockHtml(pins: readonly Pin[], names: readonly string[], units: string | undefined): string {
  const rows = pins.map((pin, i) => {
    const head = `${swatch(pin)}${escapeHtml(names[i])}`
    const trend = trendOf(pin)
    if (!trend) return block(head, [t('research.trend.tooFew', { min: MIN_TREND_POINTS })])
    const per = t(PER_KEY[trend.unit])
    const { ols, mk } = trend
    const verdictKey: MessageKey = trend.verdict === 'up' ? 'research.trend.up'
      : trend.verdict === 'down' ? 'research.trend.down'
      : trend.verdict === 'disagree' ? 'research.trend.disagree'
      : 'research.trend.none'
    const lines = [
      t(verdictKey, { pOls: formatP(ols.pValue), pMk: formatP(mk.pValue) }),
      ols.tooFewIndependent
        ? t('research.trend.olsTooFew', { slope: signedAmount(ols.slope, units), per })
        : t('research.trend.ols', { slope: signedAmount(ols.slope, units), per, low: signedAmount(ols.ciLow, units), high: signedAmount(ols.ciHigh, units), r2: formatNumber(ols.r2, { maximumFractionDigits: 2 }) }),
      t('research.trend.sen', { slope: signedAmount(mk.sen, units), per, tau: formatNumber(mk.tau, { maximumFractionDigits: 2 }) }),
      t('research.trend.n', { n: formatNumber(trend.n), neff: formatNumber(Math.max(1, Math.round(ols.nEff))) }),
    ]
    if (trend.cautions.partial) lines.push(t('research.trend.partial'))
    if (trend.cautions.lowIndependent && !ols.tooFewIndependent) lines.push(t('research.trend.lowIndependent'))
    if (trend.cautions.manyTies) lines.push(t('research.trend.manyTies'))
    if (trend.cautions.cycle) lines.push(t('research.trend.cycle'))
    return block(head, lines)
  }).join('')
  return `${rows}<p class="tools-menu-note tools-data-trend-method">${escapeHtml(t('research.trend.method'))}</p>`
}

/** Per-pin rows for the CSV, in the first trend's unit; null when no pin has a trend. */
export function trendCsvRows(pins: readonly Pin[], names: readonly string[]): Cell[][] | null {
  const trends = pins.map(trendOf)
  const unit = trends.find((tr) => tr)?.unit
  if (!unit) return null
  const per = `per_${unit}`
  const rows: Cell[][] = [[
    'pin', 'verdict', `ols_slope_${per}`, 'ols_ci95_low', 'ols_ci95_high', 'ols_p', 'ols_r2', 'ols_n_effective_nychka',
    `sen_slope_${per}`, 'sen_ci95_low_gilbert', 'sen_ci95_high_gilbert', 'mann_kendall_tau_b', 'mann_kendall_z', 'mann_kendall_p_hamed_rao',
    'hamed_rao_factor', 'n', 'cautions',
  ]]
  trends.forEach((tr, i) => {
    if (!tr) {
      rows.push([names[i], 'too few values', ...Array(15).fill(null)])
      return
    }
    const k = TREND_UNIT_MS[tr.unit] / TREND_UNIT_MS[unit]
    const cautions = Object.entries(tr.cautions).filter(([, on]) => on).map(([name]) => name).join(' ')
    rows.push([
      names[i], tr.verdict, tr.ols.slope / k, tr.ols.ciLow / k, tr.ols.ciHigh / k, tr.ols.pValue, tr.ols.r2, tr.ols.nEff,
      tr.mk.sen / k, tr.mk.senLow / k, tr.mk.senHigh / k, tr.mk.tau, tr.mk.z, tr.mk.pValue, tr.mk.correction, tr.n, cautions,
    ])
  })
  return rows
}

// ------------------------------------------------------------------ emergence (§Next S6)

/** A baseline period in whole years (inclusive). */
export interface YearPeriod {
  readonly startYear: number
  readonly endYear: number
}

export function periodMs(period: YearPeriod): { startMs: number; endMs: number } {
  return { startMs: Date.UTC(period.startYear, 0, 1), endMs: Date.UTC(period.endYear, 11, 31, 23, 59, 59) }
}

export function defaultYearPeriod(pin: Pin): YearPeriod | null {
  if (!pin.series) return null
  const d = defaultEmergencePeriod(pin.series)
  return { startYear: new Date(d.startMs).getUTCFullYear(), endYear: new Date(d.endMs).getUTCFullYear() - 1 }
}

export interface EmergenceView {
  readonly period: YearPeriod
  /** Noise the reader brought (observations, one model run); null: the baseline's own. */
  readonly noise: number | null
  /** One quantisation step of the dataset. */
  readonly quantum: number | null
  /** The dataset describes itself as a multi-model mean or median. */
  readonly ensemble: boolean
}

function settingsOf(view: EmergenceView): EmergenceSettings {
  return { period: periodMs(view.period), noise: view.noise, quantum: view.quantum }
}

function emergenceOf(pin: Pin, view: EmergenceView): PinEmergence | null {
  if (!pin.series) return null
  const r = pinEmergence(pin.series, settingsOf(view))
  return typeof r === 'string' ? null : r
}

/** The baseline period shaded, and each pin's emergence marked, on the graph. */
export function emergenceSvg(pins: readonly Pin[], frame: ChartFrame, view: EmergenceView): string {
  const xAt = (ms: number) => frame.left + Math.min(1, Math.max(0, (ms - frame.startMs) / (frame.endMs - frame.startMs))) * frame.plotW
  const p = periodMs(view.period)
  const band = `<rect x="${xAt(p.startMs).toFixed(1)}" y="${frame.top}" width="${Math.max(0, xAt(p.endMs) - xAt(p.startMs)).toFixed(1)}" height="${frame.plot}" class="tools-data-emergence-band"/>`
  const marks = pins.map((pin) => {
    const e = emergenceOf(pin, view)?.emergence
    if (!e?.emergedAt) return ''
    const x = xAt(e.emergedAt).toFixed(1)
    return `<line x1="${x}" y1="${frame.top}" x2="${x}" y2="${frame.top + frame.plot}" stroke="${escapeAttr(pin.color)}" stroke-width="1.4" stroke-dasharray="2 2" class="tools-data-emergence-mark"/>`
  }).join('')
  return band + marks
}

/** The period and noise inputs (drawn once, so typing is never interrupted by new values). */
export function emergenceControlsHtml(units: string | undefined, view: EmergenceView, bounds: { minYear: number; maxYear: number }): string {
  const yearInput = (id: string, value: number) => `<input type="number" class="tools-data-year" id="${id}" min="${bounds.minYear}" max="${bounds.maxYear}" step="1" value="${value}" aria-label="${escapeAttr(t(id.endsWith('start') ? 'research.emergence.startAria' : 'research.emergence.endAria'))}">`
  const noiseInput = `<input type="number" class="tools-data-noise" id="tools-data-noise" step="any" min="0" value="${view.noise ?? ''}" placeholder="${escapeAttr(t('research.emergence.noiseAuto'))}" aria-label="${escapeAttr(t('research.emergence.noiseAria'))}">`
  const controls = `<p class="tools-data-period">${escapeHtml(t('research.emergence.period'))} ${yearInput('tools-data-period-start', view.period.startYear)} ${escapeHtml(t('research.emergence.to'))} ${yearInput('tools-data-period-end', view.period.endYear)}</p>`
    + `<p class="tools-data-period">${escapeHtml(t('research.emergence.noise', { units: units ?? '' }))} ${noiseInput}</p>`
  return controls
}

/** The ensemble warning, one line per pin, and how it is measured (redrawn as values arrive). */
export function emergenceResultsHtml(pins: readonly Pin[], names: readonly string[], units: string | undefined, view: EmergenceView): string {
  const year = (v: number) => String(v)
  const rows = pins.map((pin, i) => {
    const head = `${swatch(pin)}${escapeHtml(names[i])}`
    if (!pin.series) return block(head, [t('research.trend.tooFew', { min: MIN_TREND_POINTS })])
    const r = pinEmergence(pin.series, settingsOf(view))
    if (r === 'short') return block(head, [t('research.emergence.short')])
    if (r === 'subAnnual') return block(head, [t('research.emergence.subAnnual')])
    if (r === 'baseline') return block(head, [t('research.emergence.noBaseline', { start: year(view.period.startYear), end: year(view.period.endYear) })])
    const e = r.emergence
    const sigma = signedAmount(e.baseSd, units).replace(/^\+/, '')
    const noiseKey: MessageKey = e.noiseFrom === 'given' ? 'research.emergence.noiseGiven' : e.noiseFrom === 'quantum' ? 'research.emergence.noiseQuantum' : 'research.emergence.noiseBaseline'
    const lines = [
      e.emergedAt !== null
        ? t(e.direction > 0 ? 'research.emergence.above' : 'research.emergence.below', { year: year(new Date(snapToFrame(e.emergedAt)).getUTCFullYear()), k: e.threshold, window: r.windowYears })
        : t('research.emergence.not', { end: year(new Date(snapToFrame(e.checkedTo ?? pin.series.endMs)).getUTCFullYear()), k: e.threshold, window: r.windowYears }),
      t(noiseKey, { sigma, start: year(view.period.startYear), end: year(view.period.endYear), n: e.baseN }),
    ]
    return block(head, lines)
  }).join('')
  const ensemble = view.ensemble && view.noise === null
    ? `<p class="analyze-change-hint">${escapeHtml(t('research.emergence.ensemble'))}</p>`
    : ''
  return `${ensemble}${rows}<p class="tools-menu-note tools-data-trend-method">${escapeHtml(t('research.emergence.method'))}</p>`
}

/** CSV rows for emergence; null when no pin has a result. */
export function emergenceCsvRows(pins: readonly Pin[], names: readonly string[], view: EmergenceView): Cell[][] | null {
  const results = pins.map((pin) => emergenceOf(pin, view))
  if (!results.some(Boolean)) return null
  return [
    ['pin', 'baseline_start_year', 'baseline_end_year', 'baseline_mean', 'noise_sd', 'noise_from', 'running_mean_years', 'threshold_sd', 'emerged_year', 'direction', 'checked_to_year'],
    ...results.map((r, i): Cell[] => r
      ? [names[i], view.period.startYear, view.period.endYear, r.emergence.baseMean, r.emergence.baseSd, r.emergence.noiseFrom, r.windowYears, r.emergence.threshold,
          r.emergence.emergedAt === null ? 'not emerged' : new Date(snapToFrame(r.emergence.emergedAt)).getUTCFullYear(), r.emergence.direction,
          r.emergence.checkedTo === null ? null : new Date(snapToFrame(r.emergence.checkedTo)).getUTCFullYear()]
      : [names[i], view.period.startYear, view.period.endYear, null, null, null, null, null, null, null, null]),
  ]
}

// ------------------------------------------------------------------ correlation (§Next S3)

/** Every pair of pins: raw, without their trends, and at the strongest lag (in years when the step is known). */
export function correlationBlockHtml(pins: readonly Pin[], names: readonly string[], stepYears: number | null): string {
  const pairs: string[] = []
  const r2 = (x: number) => formatNumber(x, { maximumFractionDigits: 2, minimumFractionDigits: 2 })
  for (let i = 0; i < pins.length; i++) {
    for (let j = i + 1; j < pins.length; j++) {
      const a = pins[i].series
      const b = pins[j].series
      const c = a && b ? pinCorrelation(a, b) : null
      const head = escapeHtml(t('research.correlation.pair', { a: names[i], b: names[j] }))
      if (!c) {
        pairs.push(block(head, [t('research.trend.tooFew', { min: MIN_TREND_POINTS })]))
        continue
      }
      const lines = [
        t('research.correlation.raw', { r: r2(c.pearson.r), p: formatP(c.pearson.pValue), rho: r2(c.spearman.r), neff: formatNumber(Math.max(1, Math.round(c.pearson.nEff))) }),
        c.detrended ? t('research.correlation.detrended', { r: r2(c.detrended.r), p: formatP(c.detrended.pValue) }) : '',
        c.bestLag && c.bestLag.lag !== 0 ? lagLine(c.bestLag, stepYears, c.bestLag.lag > 0 ? names[j] : names[i]) : '',
        c.cycle ? t('research.correlation.cycle') : '',
      ].filter(Boolean)
      pairs.push(block(head, lines))
    }
  }
  return `${pairs.join('')}<p class="tools-menu-note tools-data-trend-method">${escapeHtml(t('research.correlation.method'))}</p>`
}

/** The strongest lag, in years when the steps are, singular when the number printed is one (a year of steps is 1.0000003). */
function lagLine(best: { lag: number; r: number; pCorrected: number; tried: number }, stepYears: number | null, who: string): string {
  const steps = Math.abs(best.lag)
  const amount = stepYears !== null ? Number((steps * stepYears).toPrecision(2)) : steps
  const unit = stepYears !== null
    ? (amount === 1 ? 'research.correlation.year' : 'research.correlation.years')
    : (amount === 1 ? 'research.correlation.step' : 'research.correlation.steps')
  return t('research.correlation.lag', {
    lag: formatNumber(amount, { maximumSignificantDigits: 2 }),
    unit: t(unit),
    who,
    r: formatNumber(best.r, { maximumFractionDigits: 2, minimumFractionDigits: 2 }),
    p: formatP(best.pCorrected),
    tried: best.tried,
  })
}

/** CSV rows for every pair; null with fewer than two pins. */
export function correlationCsvRows(pins: readonly Pin[], names: readonly string[]): Cell[][] | null {
  if (pins.length < 2) return null
  const rows: Cell[][] = [[
    'pin_a', 'pin_b', 'pearson_r', 'pearson_p', 'spearman_rho', 'spearman_p', 'n', 'n_effective_bretherton',
    'detrended_r', 'detrended_p', 'best_lag_steps', 'best_lag_r', 'best_lag_p_bonferroni', 'lags_tried',
  ]]
  for (let i = 0; i < pins.length; i++) {
    for (let j = i + 1; j < pins.length; j++) {
      const a = pins[i].series
      const b = pins[j].series
      const c = a && b ? pinCorrelation(a, b) : null
      rows.push(c
        ? [names[i], names[j], c.pearson.r, c.pearson.pValue, c.spearman.r, c.spearman.pValue, c.n, c.pearson.nEff,
            c.detrended?.r ?? null, c.detrended?.pValue ?? null, c.bestLag?.lag ?? null, c.bestLag?.r ?? null, c.bestLag?.pCorrected ?? null, c.bestLag?.tried ?? null]
        : [names[i], names[j], ...Array(12).fill(null)])
    }
  }
  return rows
}

// ------------------------------------------------------------------ for the methods paragraph (§Next S5)

/** How many pins have each result now: the methods paragraph names only tests that gave one. */
export function statsCounts(pins: readonly Pin[], view: EmergenceView | null): { trend: number; unit: TrendUnit | null; emergence: number; pairs: number } {
  const trends = pins.map(trendOf).filter((tr): tr is PinTrend => tr !== null)
  const emergence = view ? pins.filter((pin) => emergenceOf(pin, view) !== null).length : 0
  let pairs = 0
  for (let i = 0; i < pins.length; i++) {
    for (let j = i + 1; j < pins.length; j++) {
      const a = pins[i].series
      const b = pins[j].series
      if (a && b && pinCorrelation(a, b)) pairs++
    }
  }
  return { trend: trends.length, unit: trends[0]?.unit ?? null, emergence, pairs }
}
